// Property-list search input — design-matched.
//
// Two visual variants, picked by responsive utilities so the same
// component fits both header layouts:
//
//  - Desktop (`md+`): an inline pill inside the page header. `bg-background`
//    against the card surface, no shadow, 280px minimum, smaller text.
//    Matches design/src/components/desktop/PropertyList.jsx lines 19-23.
//
//  - Mobile (`<md`): a full-width card with subtle shadow sitting in
//    its own row beneath the title. Larger icon + placeholder text.
//    Matches design/src/components/mobile/PropertyList.jsx lines 28-32.
//
// Writes `?search=...` to the URL with a 300 ms debounce so the list
// page can refetch without thrashing.

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";
import { Search } from "lucide-react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const DEBOUNCE_MS = 300;
const PLACEHOLDER_DESKTOP = "Tìm theo mã, toà nhà, đường…";
const PLACEHOLDER_MOBILE = "Mã, toà nhà, mô tả…";

interface PropertySearchInputProps {
  className?: string;
  /**
   * Picks the visual variant. Defaults to "desktop" because the inline
   * header is the more common host; the mobile-only header explicitly
   * passes "mobile" for its full-width card style.
   */
  variant?: "desktop" | "mobile";
}

export function PropertySearchInput({
  className,
  variant = "desktop",
}: PropertySearchInputProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const current = searchParams.get("search") ?? "";

  // Local draft mirrors the URL value but stays editable. When the URL
  // changes externally (advanced filter modal's "Đặt lại" + Apply, say)
  // we reset the draft during render — the React-recommended pattern
  // for adjusting state in response to a prop change, which avoids the
  // cascading-render lint that `useEffect(() => setDraft(...))` triggers.
  const [draft, setDraft] = useState(current);
  const [lastSeenUrlValue, setLastSeenUrlValue] = useState(current);
  if (current !== lastSeenUrlValue) {
    setLastSeenUrlValue(current);
    setDraft(current);
  }

  // Debounced commit: only push to the URL once the user stops typing.
  useEffect(() => {
    if (draft === current) return;
    const handle = window.setTimeout(() => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (draft.trim().length === 0) {
            next.delete("search");
          } else {
            next.set("search", draft.trim());
          }
          next.delete("page");
          return next;
        },
        { replace: true },
      );
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [draft, current, setSearchParams]);

  const placeholder =
    variant === "mobile" ? PLACEHOLDER_MOBILE : PLACEHOLDER_DESKTOP;

  return (
    <div
      className={cn(
        "relative flex items-center gap-2.5 border border-border",
        "focus-within:border-ring focus-within:ring-2 focus-within:ring-ring/30",
        variant === "mobile"
          ? "rounded-[12px] bg-card px-3.5 py-2.5 shadow-[var(--shadow-soft)]"
          : "h-[34px] rounded-[10px] bg-background px-3",
        className,
      )}
    >
      <Search
        aria-hidden="true"
        className={cn(
          "shrink-0 text-muted-foreground",
          variant === "mobile" ? "size-4" : "size-3.5",
        )}
      />
      <Input
        type="search"
        inputMode="search"
        placeholder={placeholder}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        aria-label="Tìm kiếm bất động sản"
        className={cn(
          "h-full flex-1 border-0 bg-transparent p-0 shadow-none",
          "focus-visible:ring-0 focus-visible:ring-offset-0",
          "placeholder:text-muted-foreground",
          variant === "mobile" ? "text-[14px]" : "text-[12px]",
        )}
      />
      <kbd
        className={cn(
          "shrink-0 rounded border border-border font-mono text-ink-faint",
          variant === "mobile"
            ? "px-1.5 py-0.5 text-[11px]"
            : "px-1.5 py-px text-[11px]",
        )}
      >
        ⌘K
      </kbd>
    </div>
  );
}
